import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  repairCalibreImageNames,
  type MisnamedCalibreImage,
  type RepairCalibreImageNamesResult,
} from '../../services/firebase';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { CollapsibleSection } from './CollapsibleSection';

const STATUS_LABEL: Record<MisnamedCalibreImage['status'], string> = {
  ready: 'To repair',
  copied: 'Copied',
  orphan: 'Unreferenced',
  blocked: 'Needs a person',
};
const STATUS_VARIANT: Record<MisnamedCalibreImage['status'], 'info' | 'success' | 'default' | 'danger'> = {
  ready: 'info',
  copied: 'success',
  orphan: 'default',
  blocked: 'danger',
};

function Stat({ label, value, danger }: { label: string; value: number | string; danger?: boolean }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-lg font-semibold ${danger ? 'text-red-600' : 'text-gray-900'}`}>{value}</div>
    </div>
  );
}

const code = (text: string) => <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">{text}</code>;

/**
 * Data Maintenance → Tools → Repair calibre image names.
 *
 * Some calibre images were stored as `{timestamp}.images` / `.image` because
 * the uploaders took the extension from a dot-less URL segment. The resize
 * extension names thumbnails from the source name, so those images have no
 * usable thumbnail. This drives the `repairCalibreImageNames` callable through
 * its three steps: dry run → repair → (later) clean up.
 *
 * The panel only ever acts on the report it is showing: each writing step sends
 * the token that came with that report, and the callable refuses it if the
 * bucket has changed since.
 */
export function RepairCalibreImageNamesSection() {
  const [running, setRunning] = useState<null | 'dryRun' | 'repair' | 'cleanup'>(null);
  const [report, setReport] = useState<RepairCalibreImageNamesResult | null>(null);
  const [confirm, setConfirm] = useState<null | 'repair' | 'cleanup'>(null);

  const run = async (mode: 'dryRun' | 'repair' | 'cleanup') => {
    setRunning(mode);
    try {
      const result = await repairCalibreImageNames({
        mode,
        ...(mode === 'repair' ? { confirmationToken: report?.repairToken } : {}),
        ...(mode === 'cleanup' ? { confirmationToken: report?.cleanupToken } : {}),
      });
      setReport(result);
      if (mode === 'dryRun') {
        toast.success(
          result.misnamed.length === 0
            ? 'Dry run: no misnamed calibre images'
            : `Dry run: ${result.misnamed.length} misnamed, ${result.repairCount} to repair, ${result.cleanupCount} ready to clean up`,
        );
      } else if (mode === 'repair') {
        const r = result.repaired;
        const failed = r?.failures.length ?? 0;
        (failed ? toast.error : toast.success)(
          `Repaired: ${r?.copied ?? 0} copied, ${r?.pathsUpdated ?? 0} reference(s) updated` +
            (failed ? ` — ${failed} failed, see the report` : ''),
        );
      } else {
        const c = result.cleaned;
        const failed = c?.failures.length ?? 0;
        (failed ? toast.error : toast.success)(
          `Cleaned up: ${c?.originalsDeleted ?? 0} old file(s) and ${c?.variantsDeleted ?? 0} old thumbnail(s) deleted` +
            (failed ? ` — ${failed} failed, see the report` : ''),
        );
      }
    } catch (err) {
      console.error('repairCalibreImageNames failed:', err);
      toast.error(err instanceof Error ? err.message : 'Repair failed — see console');
    } finally {
      setRunning(null);
    }
  };

  const busy = running !== null;
  const failures = [...(report?.repaired?.failures ?? []), ...(report?.cleaned?.failures ?? [])];
  const waitingOnThumbnails =
    report?.misnamed.filter((m) => m.newObjectExists && m.references.length === 0 && !m.cleanable).length ?? 0;

  return (
    <CollapsibleSection sectionId="repairCalibreImageNames" title="Repair calibre image names">
      <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-sm text-gray-600">
          Some calibre images were uploaded as {code('{timestamp}.images')} or {code('.image')} instead of{' '}
          {code('.jpg')} / {code('.png')}. The resize extension names thumbnails after the file, so those images
          have no usable thumbnail and the app falls back to the full-size original. New uploads are named
          correctly; this fixes the ones already stored.
        </p>
        <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-600">
          <li>
            <strong>Dry run</strong> lists what is wrong and changes nothing.
          </li>
          <li>
            <strong>Repair</strong> copies each file to its proper name and points the calibre at it. The copy is
            what makes the thumbnails appear. Nothing is deleted.
          </li>
          <li>
            <strong>Clean up</strong>, a minute or more later, deletes the old file and its misnamed thumbnails —
            only where the new file and both new thumbnails exist and no calibre still uses the old one.
          </li>
        </ol>

        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" loading={running === 'dryRun'} disabled={busy} onClick={() => void run('dryRun')}>
            Dry run
          </Button>
          <Button
            variant="primary"
            loading={running === 'repair'}
            disabled={busy || !report || report.repairCount === 0}
            title={report ? undefined : 'Run a dry run first'}
            onClick={() => setConfirm('repair')}
          >
            Repair{report && report.repairCount > 0 ? ` ${report.repairCount}` : ''}
          </Button>
          <Button
            variant="danger"
            loading={running === 'cleanup'}
            disabled={busy || !report || report.cleanupCount === 0}
            title={report ? undefined : 'Run a dry run first'}
            onClick={() => setConfirm('cleanup')}
          >
            Clean up{report && report.cleanupCount > 0 ? ` ${report.cleanupCount}` : ''}
          </Button>
        </div>

        {report && (
          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-gray-900">
                {report.mode === 'dryRun' ? 'Dry run' : report.mode === 'repair' ? 'After repair' : 'After clean-up'}
              </span>
              <Badge variant={report.mode === 'dryRun' ? 'info' : 'success'}>
                {report.mode === 'dryRun' ? 'no writes' : 'written'}
              </Badge>
              <span className="text-xs text-gray-500">
                bucket {report.bucket} · {report.objectsListed.toLocaleString()} objects under calibres/ ·{' '}
                {report.calibreDocsRead.toLocaleString()} calibre documents read
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat label="Misnamed images" value={report.misnamed.length} />
              <Stat label="To repair" value={report.repairCount} />
              <Stat label="Ready to clean up" value={report.cleanupCount} />
              <Stat
                label="Need a person"
                value={report.misnamed.filter((m) => m.status === 'blocked' || m.status === 'orphan').length}
                danger={report.misnamed.some((m) => m.status === 'blocked')}
              />
            </div>

            {report.repaired && (
              <p className="text-xs text-gray-700">
                Repair: {report.repaired.copied} copied, {report.repaired.alreadyCopied} already copied,{' '}
                {report.repaired.pathsUpdated} reference(s) updated across {report.repaired.docsUpdated} calibre
                document(s). Thumbnails for the new files are generated in the background — run a dry run in a
                minute or two before cleaning up.
              </p>
            )}
            {report.cleaned && (
              <p className="text-xs text-gray-700">
                Clean-up: {report.cleaned.originalsDeleted} old file(s) and {report.cleaned.variantsDeleted} old
                thumbnail(s) deleted.
              </p>
            )}
            {waitingOnThumbnails > 0 && (
              <p className="text-xs text-amber-800">
                {waitingOnThumbnails} repaired file(s) are still waiting for their new thumbnails. Run a dry run
                again shortly; they become ready to clean up once both thumbnails exist.
              </p>
            )}
            {failures.length > 0 && (
              <div role="alert" className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                <div className="font-semibold">{failures.length} step(s) failed</div>
                <ul className="mt-1 space-y-0.5">
                  {failures.map((f, i) => (
                    <li key={i} className="break-all">
                      {f.oldPath}: {f.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {report.misnamed.length > 0 ? (
              <div className="overflow-x-auto rounded border border-gray-200 bg-white">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-left uppercase tracking-wide text-gray-500">
                      <th className="px-2 py-1.5 font-medium">Stored as</th>
                      <th className="px-2 py-1.5 font-medium">Becomes</th>
                      <th className="px-2 py-1.5 font-medium">Type</th>
                      <th className="px-2 py-1.5 font-medium">Used by</th>
                      <th className="px-2 py-1.5 font-medium">New thumbnails</th>
                      <th className="px-2 py-1.5 font-medium">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.misnamed.map((m) => (
                      <tr key={m.oldPath} className="border-t border-gray-100 align-top">
                        <td className="break-all px-2 py-1.5 font-mono">{m.oldPath}</td>
                        <td className="break-all px-2 py-1.5 font-mono">
                          {m.newPath ? m.newPath.slice(m.newPath.lastIndexOf('/') + 1) : '—'}
                        </td>
                        <td className="px-2 py-1.5">{m.contentType ?? '—'}</td>
                        <td className="px-2 py-1.5">
                          {m.references.length === 0
                            ? m.newObjectExists
                              ? 'nothing (repointed)'
                              : 'no calibre'
                            : m.references.map((r) => `${r.docId} (${r.field}[${r.index}])`).join(', ')}
                        </td>
                        <td className="px-2 py-1.5">{m.newObjectExists ? `${m.newVariantsPresent} of 2` : '—'}</td>
                        <td className="px-2 py-1.5">
                          <Badge variant={m.cleanable ? 'success' : STATUS_VARIANT[m.status]}>
                            {m.cleanable ? 'Ready to clean up' : STATUS_LABEL[m.status]}
                          </Badge>
                          {(m.blockReason || (!m.cleanable && m.status === 'copied' && m.cleanupBlockedBy)) && (
                            <div className="mt-0.5 text-gray-500">{m.blockReason ?? m.cleanupBlockedBy}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-emerald-700">No misnamed calibre images — nothing to repair.</p>
            )}

            <p className="text-xs text-gray-600">
              Thumbnail check on correctly named calibre images: {report.thumbnails.complete.toLocaleString()} of{' '}
              {report.thumbnails.originalsChecked.toLocaleString()} have both thumbnails.
              {report.thumbnails.missingCount > 0 &&
                ` ${report.thumbnails.missingCount.toLocaleString()} do not — this tool does not change those.`}
            </p>
            {report.thumbnails.missing.length > 0 && (
              <details className="text-xs text-gray-600">
                <summary className="cursor-pointer">
                  Show {report.thumbnails.missing.length}
                  {report.thumbnails.missingCount > report.thumbnails.missing.length ? ' (first few)' : ''} without
                  thumbnails
                </summary>
                <ul className="mt-1 space-y-0.5 font-mono">
                  {report.thumbnails.missing.map((path) => (
                    <li key={path} className="break-all">
                      {path}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {report.otherTrees?.map((tree) => (
              <div key={tree.prefix} className="text-xs text-gray-600">
                <span className={tree.misnamedCount > 0 ? 'font-semibold text-amber-800' : undefined}>
                  {tree.prefix}: {tree.misnamedCount.toLocaleString()} misnamed of{' '}
                  {tree.originalsScanned.toLocaleString()} originals
                </span>
                {!tree.complete && ' (scan stopped at the time limit — at least this many)'}
                {tree.misnamedCount > 0 && ' — reported only; this tool repairs calibres/.'}
                {tree.sample.length > 0 && (
                  <details>
                    <summary className="cursor-pointer">Show {tree.sample.length}</summary>
                    <ul className="mt-1 space-y-0.5 font-mono">
                      {tree.sample.map((path) => (
                        <li key={path} className="break-all">
                          {path}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm === 'cleanup' ? 'Delete the old files?' : 'Repair calibre image names?'}
      >
        <div className="space-y-3">
          {confirm === 'repair' && (
            <p className="text-sm text-gray-700">
              <strong>{report?.repairCount ?? 0}</strong> image(s) will be copied to a correctly named file and the
              calibres that use them pointed at the copy. Nothing is deleted. Each calibre written is re-indexed.
            </p>
          )}
          {confirm === 'cleanup' && (
            <p className="text-sm text-gray-700">
              <strong>{report?.cleanupCount ?? 0}</strong> old file(s) and their misnamed thumbnails will be{' '}
              <strong>permanently deleted</strong>. Each has a correctly named copy with both thumbnails, and no
              calibre uses the old file any more. This cannot be undone.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant={confirm === 'cleanup' ? 'danger' : 'primary'}
              onClick={() => {
                const mode = confirm;
                setConfirm(null);
                if (mode) void run(mode);
              }}
            >
              {confirm === 'cleanup' ? 'Delete old files' : 'Repair'}
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}
